import { Injectable } from '@angular/core';
import { DataHelper } from '@cdw-ae/common-frontend-data';
import {
  FieldMatcher,
  FilterBarField,
  SelectedFilterField,
  isRelativeDateTimeMatcherOption,
} from '@cdw-ae/filter-bar-interfaces';
import { UrlStateItem } from '../interfaces/url-routing.model';
import { UrlParamStrategy } from './url-param-strategy';

/**
 * Relative dateTime options (see isRelativeDateTimeMatcherOption) carry no date of their
 * own. They serialize to the bare option name (`enrolledTime:gt:lastHour`) instead of a
 * timestamp so the filter stays relative to when the URL is opened rather than when it
 * was copied.
 */

/**
 * Guards the `new Date(...)` calls below. A URL param is user-editable and can be stale,
 * truncated, or hand-typed, and `new Date('nonsense').toISOString()` throws a RangeError
 * that no caller of convertToInput catches — which takes down the whole filter-bar
 * restore instead of dropping the one bad filter.
 */
const toIsoStringOrNull = (value: unknown): string | null => {
  const date = new Date(String(value));
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

@Injectable({
  providedIn: 'root',
})
export class FilterBarParamStrategy
  implements
    UrlParamStrategy<SelectedFilterField, FilterBarField, string | object>
{
  public convertToInput(
    params: UrlStateItem<string | object | unknown>[],
    settings: FilterBarField[]
  ): SelectedFilterField[] {
    return params
      .map((param) => {
        const filterBarField = settings.find((f) => f.name === param.field);
        if (!filterBarField) return null;

        const selectedMatcher = this.getMatcherForOperator(
          filterBarField,
          param.operator
        );
        if (!selectedMatcher) return null;

        const matchValue = this.buildMatchValue(param, selectedMatcher);
        if (matchValue === null) return null;

        return {
          uid: DataHelper.makeUUID(),
          field: filterBarField,
          selectedMatcher,
          matchValue,
        };
      })
      .filter(Boolean) as SelectedFilterField[];
  }

  public convertFromInput(
    input: SelectedFilterField[]
  ): UrlStateItem<string | object>[] {
    return input.map((selectedFilter) => {
      const field = selectedFilter.field.name;
      const parseResult = this.parseMatchValue(selectedFilter);

      const criteria: UrlStateItem<string | object> = {
        field,
        operator: parseResult.operator,
        value: parseResult.value as string | object,
      };

      if (parseResult.value2 !== undefined) {
        criteria.value2 = parseResult.value2 as string | object;
      }

      return criteria;
    });
  }

  protected buildMatchValue(
    param: UrlStateItem<string | object | unknown>,
    selectedMatcher: FieldMatcher
  ): string | object | null {
    if (selectedMatcher.name === 'dateTime') {
      // null when the URL carried an unparsable date; convertToInput drops the filter.
      return this.buildDateTimeMatchValue(param);
    }

    if (selectedMatcher.name === 'numberMatch') {
      return this.buildNumberMatchValue(param);
    }

    if (selectedMatcher.name === 'enhancedText') {
      return this.buildEnhancedTextMatchValue(param);
    }

    if (selectedMatcher.name === 'selectFrom') {
      return this.buildSelectFromMatchValue(param);
    }

    return param.operator === 'between'
      ? `${param.value} - ${param.value2}`
      : String(param.value);
  }

  protected parseMatchValue(selectedFilter: SelectedFilterField): {
    operator: UrlStateItem['operator'];
    value: unknown;
    value2?: unknown;
  } {
    if (selectedFilter.selectedMatcher.name === 'dateTime') {
      return this.parseDateTimeMatchValue(selectedFilter.matchValue as string);
    }

    if (selectedFilter.selectedMatcher.name === 'numberMatch') {
      return this.parseNumberMatchValue(
        selectedFilter.matchValue as { operator: string; value: number }
      );
    }

    if (selectedFilter.selectedMatcher.name === 'enhancedText') {
      return this.parseEnhancedTextMatchValue(
        selectedFilter.matchValue as { operator: string; value: string }
      );
    }

    return {
      operator: this.getOperatorForMatcher(selectedFilter.selectedMatcher.name),
      value: selectedFilter.matchValue,
    };
  }

  protected buildSelectFromMatchValue(
    param: UrlStateItem<string | object | unknown>
  ): string {
    const value = String(param.value);
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return value;
    } catch {
      // Not valid JSON — wrap in array below
    }
    return JSON.stringify([value]);
  }

  protected buildNumberMatchValue(
    param: UrlStateItem<string | object | unknown>
  ): { operator: string; value: number } {
    // Convert URL operator to display operator
    const operatorMap: Record<UrlStateItem['operator'], string> = {
      equals: '=',
      greaterThan: '>',
      lessThan: '<',
      greaterThanOrEqual: '>=',
      lessThanOrEqual: '<=',
      between: 'between',
      startsWith: '=',
      contains: '=',
      oneOf: 'oneOf',
      is: 'is',
      isNot: 'not',
    };

    return {
      operator: operatorMap[param.operator] || '=',
      value: Number(param.value),
    };
  }

  protected buildEnhancedTextMatchValue(
    param: UrlStateItem<string | object | unknown>
  ): {
    operator: string;
    value: string;
  } {
    // Convert URL operator to display operator
    const operatorMap: Record<UrlStateItem['operator'], string> = {
      equals: '=',
      greaterThan: '>',
      lessThan: '<',
      greaterThanOrEqual: '>=',
      lessThanOrEqual: '<=',
      between: 'between',
      startsWith: 'startsWith',
      contains: 'contains',
      oneOf: 'oneOf',
      is: 'is',
      isNot: 'not',
    };

    return {
      operator: operatorMap[param.operator] || '=',
      value: String(param.value),
    };
  }

  protected parseNumberMatchValue(matchValue: {
    operator: string;
    value: number;
  }): {
    operator: UrlStateItem['operator'];
    value: unknown;
    value2?: unknown;
  } {
    // Convert display operator to URL operator
    const operatorMap: Record<string, UrlStateItem['operator']> = {
      '=': 'equals',
      '>': 'greaterThan',
      '<': 'lessThan',
      '>=': 'greaterThanOrEqual',
      '<=': 'lessThanOrEqual',
    };

    return {
      operator: operatorMap[matchValue.operator] || 'equals',
      value: matchValue.value,
    };
  }

  protected parseEnhancedTextMatchValue(
    matchValue: { operator: string; value: string } | string
  ): {
    operator: UrlStateItem['operator'];
    value: unknown;
    value2?: unknown;
  } {
    // Handle case where matchValue is just a string (backward compatibility)
    if (typeof matchValue === 'string') {
      return {
        operator: 'equals',
        value: matchValue,
      };
    }

    // Convert display operator to URL operator
    const operatorMap: Record<string, UrlStateItem['operator']> = {
      '=': 'equals',
      '>': 'greaterThan',
      '<': 'lessThan',
      '>=': 'greaterThanOrEqual',
      '<=': 'lessThanOrEqual',
      startsWith: 'startsWith',
      contains: 'contains',
    };

    return {
      operator: operatorMap[matchValue.operator] || 'equals',
      value: matchValue.value,
    };
  }

  protected buildDateTimeMatchValue(
    param: UrlStateItem<string | object | unknown>
  ): string | null {
    // Relative options ("in the last hour/day/week") have no date to parse — they round
    // trip as the bare option name. Handle them before the operator strategies below,
    // which would otherwise run toIsoStringOrNull('lastHour') and silently drop the
    // filter (null) instead of preserving it as a relative window.
    if (isRelativeDateTimeMatcherOption(param.value)) {
      return JSON.stringify({ option: param.value, value: true });
    }

    // Every remaining option needs a real date out of the URL. These return null rather
    // than throwing so one unparsable param drops only its own filter — see
    // toIsoStringOrNull.
    const asOption = (option: string) => (): string | null => {
      const value = toIsoStringOrNull(param.value);
      return value === null ? null : JSON.stringify({ option, value });
    };
    const since = asOption('sinceDateTime');

    const strategies: Record<UrlStateItem['operator'], () => string | null> = {
      between: () =>
        toIsoStringOrNull(param.value) === null ||
        toIsoStringOrNull(param.value2) === null
          ? null
          : JSON.stringify({
              option: 'betweenDateTime',
              value: { start: param.value, end: param.value2 },
            }),
      greaterThan: since,
      greaterThanOrEqual: since,
      lessThan: asOption('beforeDateTime'),
      lessThanOrEqual: asOption('beforeDateTime'),
      equals: since,
      startsWith: since,
      contains: since,
      oneOf: () => String(param.value),
      is: () => String(param.value),
      isNot: () => String(param.value),
    };

    return strategies[param.operator]();
  }

  protected parseDateTimeMatchValue(matchValue: string): {
    operator: UrlStateItem['operator'];
    value: unknown;
    value2?: unknown;
  } {
    try {
      const dateTimeValue = JSON.parse(matchValue);
      const parsers: Record<
        string,
        () => { operator: UrlStateItem['operator']; value: unknown; value2?: unknown }
      > = {
        betweenDateTime: () => ({
          operator: 'between' as const,
          value: dateTimeValue.value.start,
          value2: dateTimeValue.value.end,
        }),
        sinceDateTime: () => ({
          operator: 'greaterThan' as const,
          value: new Date(dateTimeValue.value).toISOString(),
        }),
        beforeDateTime: () => ({
          operator: 'lessThan' as const,
          value: new Date(dateTimeValue.value).toISOString(),
        }),
      };

      if (isRelativeDateTimeMatcherOption(dateTimeValue.option)) {
        // 'greaterThan' is the operator that resolves back to the dateTime matcher in
        // getMatcherForOperator(), and matches the semantics of "since N ago".
        return {
          operator: 'greaterThan' as const,
          value: dateTimeValue.option,
        };
      }

      const parser = parsers[dateTimeValue.option];
      return parser
        ? parser()
        : {
            operator: 'equals' as const,
            value: matchValue,
          };
    } catch (_error) {
      // Silent error handling - return default value
      return { operator: 'equals', value: matchValue };
    }
  }

  protected getMatcherForOperator(
    field: FilterBarField,
    operator: UrlStateItem['operator']
  ): FieldMatcher | null {
    const matchers: Record<
      UrlStateItem['operator'],
      () => FieldMatcher | undefined
    > = {
      equals: () =>
        field.fieldMatchers.find((m) => m.name === 'fullMatch') ||
        field.fieldMatchers.find((m) => m.name === 'numberMatch') ||
        field.fieldMatchers.find((m) => m.name === 'enhancedText'),
      startsWith: () =>
        field.fieldMatchers.find((m) => m.name === 'startsWith') ||
        field.fieldMatchers.find((m) => m.name === 'enhancedText'),
      between: () => field.fieldMatchers.find((m) => m.name === 'dateTime'),
      greaterThan: () =>
        field.fieldMatchers.find((m) => m.name === 'dateTime') ||
        field.fieldMatchers.find((m) => m.name === 'numberMatch') ||
        field.fieldMatchers.find((m) => m.name === 'enhancedText'),
      greaterThanOrEqual: () =>
        field.fieldMatchers.find((m) => m.name === 'numberMatch') ||
        field.fieldMatchers.find((m) => m.name === 'enhancedText'),
      lessThan: () =>
        field.fieldMatchers.find((m) => m.name === 'dateTime') ||
        field.fieldMatchers.find((m) => m.name === 'numberMatch') ||
        field.fieldMatchers.find((m) => m.name === 'enhancedText'),
      lessThanOrEqual: () =>
        field.fieldMatchers.find((m) => m.name === 'numberMatch') ||
        field.fieldMatchers.find((m) => m.name === 'enhancedText'),
      contains: () =>
        field.fieldMatchers.find((m) => m.name === 'contains') ||
        field.fieldMatchers.find((m) => m.name === 'enhancedText'),
      oneOf: () =>
        field.fieldMatchers.find((m) => m.name === 'oneOf') ||
        field.fieldMatchers.find((m) => m.name === 'selectFrom'),
      is: () =>
        field.fieldMatchers.find((m) => m.name === 'is'),
      isNot: () =>
        field.fieldMatchers.find((m) => m.name === 'isNot'),
    };

    const matcher = matchers[operator];
    return matcher
      ? matcher() || field.fieldMatchers[0] || null
      : field.fieldMatchers[0] || null;
  }

  protected getOperatorForMatcher(
    matcherName: string
  ): UrlStateItem['operator'] {
    const operators: Record<string, UrlStateItem['operator']> = {
      startsWith: 'startsWith' as const,
      fullMatch: 'equals' as const,
      dateTime: 'greaterThan' as const,
      numberMatch: 'equals' as const,
      enhancedText: 'equals' as const,
      contains: 'contains' as const,
      selectFrom: 'oneOf' as const,
      is: 'is' as const,
      isNot: 'isNot' as const,
    };

    return operators[matcherName] || 'equals';
  }
}
